/**
 * district 設定の JSON Import/Export (staging→本番 移送用)。
 *
 * 背景 (JSON I/O 監査): 従来 district 設定そのものを JSON で持ち出す手段が無く、移送は手作業 or
 * Firestore 直コピーしか無かった。本モジュールで「地区設定だけ」を環境間で安全に移送できるようにする。
 *
 * 設計方針:
 *   - **項目を落とさない**ため blacklist 方式: doc 全体を verbatim とし、env 固有 field のみ除去。
 *     whitelist だと型外 field (installPromptEnabled / surveyUrl / allowPostingWithoutSession /
 *     userProfileOptions) を取りこぼすため。
 *   - env 固有 (移送先で再生成 or 不正になる) field は export で除去し import で regenerate:
 *       id / urlToken / createdAt / updatedAt / createdBy / updatedBy / archivedAt / schemaVersion / version / mapPmtiles
 *     (mapPmtiles は staging Storage パスを指す dangling 参照になるため除去 → 移送先で再生成する)
 *   - postAreaCenter は Firestore GeoPoint。JSON.stringify では getter が落ちるため {latitude,longitude}
 *     に正規化して export、import で GeoPoint に復元する。
 *   - import は **常に新規地区を作成** (新 id + 新 urlToken)。既存への上書きはしない (誤上書き防止)。
 *   - 参照 (aiAnalysis.settingsId / outputFormatIdOverride / categoryMasterIdsOverride /
 *     analysisDisplayConfigId 等) は ID ベースでそのまま保持される → 移送先に **同 id の master を
 *     先に投入** しておくこと (M1〜M5 の JSON 取込)。さもないと AI 解析が dangling 参照になる。
 *   - aiVerification.activeTemplateId は district サブコレクション参照のため、検証テンプレ本体は別途移送が必要
 *     (本 I/O の対象外。設定値自体は保持する)。
 *
 * features 境界 (ESLint boundaries): 他 feature の deep import を避けるため、汎用 JSON helper を
 * masters/ から借りず本モジュール内に self-contained で実装する。envelope schema は masters と互換
 * ({exportedAt, schemaVersion, items[]})。
 */

import { GeoPoint, collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { generateUrlToken } from './useDistricts';

/** envelope schema バージョン (masters の MASTER_IO_SCHEMA_VERSION と互換)。 */
export const DISTRICT_IO_SCHEMA_VERSION = '1' as const;

/** 1 ファイルあたりの import 上限件数 (暴走防御)。地区数が数百を超える運用は無いため十分。 */
export const MAX_DISTRICT_IMPORT = 200;

/** import 時に必ず除去/再生成する env 固有 field。 */
export const DISTRICT_ENV_FIELDS = [
  'id',
  'urlToken',
  'createdAt',
  'updatedAt',
  'createdBy',
  'updatedBy',
  'archivedAt',
  'schemaVersion',
  'version',
  'mapPmtiles',
] as const;

export type DistrictConfigExport = Record<string, unknown>;

export interface DistrictIoEnvelope {
  exportedAt: string;
  schemaVersion: string;
  items: DistrictConfigExport[];
}

export interface DistrictParseResult {
  items: DistrictConfigExport[];
  errors: string[];
}

/** GeoPoint (または {latitude,longitude} 様 object) を JSON-safe な平坦 object に正規化。 */
function geoToPlain(v: unknown): unknown {
  if (v && typeof v === 'object') {
    const g = v as { latitude?: unknown; longitude?: unknown };
    if (typeof g.latitude === 'number' && typeof g.longitude === 'number') {
      return { latitude: g.latitude, longitude: g.longitude };
    }
  }
  return v;
}

/**
 * district doc (full, id 込) → 移送用 config。
 * env field を除去し postAreaCenter を平坦化、他は verbatim 保持 (型外 field も落とさない)。
 */
export function buildDistrictConfigExport(district: Record<string, unknown>): DistrictConfigExport {
  const clone: Record<string, unknown> = { ...district };
  for (const f of DISTRICT_ENV_FIELDS) delete clone[f];
  if (clone.postAreaCenter !== undefined) {
    clone.postAreaCenter = geoToPlain(clone.postAreaCenter);
  }
  return clone;
}

/** config[] を envelope 化 (pure)。 */
export function serializeDistrictExport(configs: DistrictConfigExport[]): DistrictIoEnvelope {
  return {
    exportedAt: new Date().toISOString(),
    schemaVersion: DISTRICT_IO_SCHEMA_VERSION,
    items: configs,
  };
}

/** 移送用 config の最小 validator。name (非空) と mode (string) を必須、他は緩く許容。 */
export function validateDistrictConfig(raw: unknown): DistrictConfigExport | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.name !== 'string' || !r.name.trim()) return null;
  if (typeof r.mode !== 'string' || !r.mode.trim()) return null;
  return r;
}

/**
 * import 生 JSON (envelope / bare array / 単一 object) を validate して config[] に変換。
 * 失敗 item は errors に集約して skip (部分成功許容)。masters の parseImportedMasters と同じ受理形式。
 */
export function parseDistrictImport(raw: unknown): DistrictParseResult {
  const errors: string[] = [];
  let rawItems: unknown[] = [];
  if (Array.isArray(raw)) {
    rawItems = raw;
  } else if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    rawItems = Array.isArray(obj.items) ? obj.items : [raw];
  } else {
    return { items: [], errors: ['JSON の形式が不正です (object または array が必要)'] };
  }
  if (rawItems.length > MAX_DISTRICT_IMPORT) {
    return {
      items: [],
      errors: [`件数上限 (${MAX_DISTRICT_IMPORT} 件) を超えています: ${rawItems.length} 件`],
    };
  }
  const items: DistrictConfigExport[] = [];
  rawItems.forEach((rawItem, index) => {
    const v = validateDistrictConfig(rawItem);
    if (v === null) {
      errors.push(`Item #${index + 1}: name / mode が不正のため skip`);
      return;
    }
    items.push(v);
  });
  return { items, errors };
}

/** JSON を Blob で download 起動 (副作用: DOM)。 */
export function downloadDistrictJson(data: unknown, filename: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * 移送用 config から新規地区を作成 (新 id + 新 urlToken + serverTimestamp)。
 * env field を再除去し postAreaCenter を GeoPoint に復元、他は verbatim。
 * config は JSON.parse 由来のため undefined を含まない (pruneUndefinedDeep 不要)。
 * 認可: firestore.rules `allow create: if isMaster()`。
 */
export async function importDistrictFromConfig(
  config: DistrictConfigExport,
  uid: string,
): Promise<string> {
  const id = crypto.randomUUID();
  const ref = doc(collection(db, 'districts'), id);
  const now = serverTimestamp();

  const clone: Record<string, unknown> = { ...config };
  for (const f of DISTRICT_ENV_FIELDS) delete clone[f];
  if (clone.postAreaCenter && typeof clone.postAreaCenter === 'object') {
    const p = clone.postAreaCenter as { latitude?: unknown; longitude?: unknown };
    if (typeof p.latitude === 'number' && typeof p.longitude === 'number') {
      clone.postAreaCenter = new GeoPoint(p.latitude, p.longitude);
    } else {
      delete clone.postAreaCenter;
    }
  }

  // District は MasterCommon を継承せず `version` 必須要件が無いため、env 除去したまま再設定しない
  // (master の upsertMaster とは異なり version=1 を付与しない)。createDistrict と同じく schemaVersion のみ。
  const payload: Record<string, unknown> = {
    ...clone,
    id,
    urlToken: generateUrlToken(),
    createdAt: now,
    createdBy: uid,
    updatedAt: now,
    updatedBy: uid,
    archivedAt: null,
    schemaVersion: 1,
  };
  await setDoc(ref, payload);
  return id;
}
